import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { containTab, enterModalLayer, focusInto, inertSiblings, restoreFocus } from "@mmm/ui";
interface ModalProps {
  onClose?: () => void;
  children: ReactNode;
  width?: number | string;
  className?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

export function Modal({ onClose, children, width, className = "", ariaLabel, ariaLabelledBy }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const overlay = overlayRef.current;
    const dialog = dialogRef.current;
    if (!overlay || !dialog) return;

    const layer = enterModalLayer();
    const returnFocusTo = document.activeElement as HTMLElement | null;
    focusInto(dialog);
    const restoreSiblings = inertSiblings(overlay);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!layer.isTop()) return;
      if (event.key === "Escape") {
        onCloseRef.current?.();
        return;
      }
      if (event.key === "Tab") containTab(dialog, event);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      layer.release();
      restoreSiblings();
      restoreFocus(returnFocusTo);
    };
  }, []);

  return createPortal(
    <div
      ref={overlayRef}
      className="mmm-overlay"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`mmm-modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        style={width ? { width } : undefined}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
